import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { itineraryApi } from '../services/api';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedBlogPage = () => {
  const { shareId } = useParams();
  const [blog, setBlog] = useState(null);
  const [tripInput, setTripInput] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSharedBlog = useCallback(async () => {
    try {
      const res = await itineraryApi.getSharedBlog(shareId);
      if (res.data.success) {
        setBlog(res.data.blog);
        setTripInput(res.data.trip_input);
      }
    } catch {
      setError('This blog is no longer available.');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadSharedBlog();
  }, [loadSharedBlog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 text-center">
          <p className="text-white/60 text-lg">{error || 'Blog not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="shared-blog-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B3C5D] via-[#0d4a6b] to-[#1a6b8a]" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/8 rounded-full blur-3xl animate-pulse" />
      </div>
      <Header />
      <main className="pt-24 pb-16 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Cover Image */}
          {blog.cover_image && (
            <div className="rounded-2xl overflow-hidden shadow-2xl mb-8">
              {blog.cover_image.startsWith('triplea-travel/') ? (
                <img
                  src={`${API_URL}/api/blog/cover/${encodeURIComponent(blog.cover_image)}`}
                  alt="Blog cover"
                  className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                />
              ) : (
                <img
                  src={`${API_URL}/api/blog/cover/${encodeURIComponent(blog.cover_image)}`}
                  alt="Blog cover"
                  className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                />
              )}
            </div>
          )}

          {/* Title */}
          <div className="text-center mb-12">
            <p className="text-gold text-sm tracking-[0.2em] uppercase font-semibold mb-3">Travel Blog</p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white drop-shadow-lg mb-3 leading-tight">{blog.title}</h1>
            {blog.subtitle && <p className="text-white/50 text-lg italic">{blog.subtitle}</p>}
            <div className="w-16 h-0.5 bg-gold/40 mx-auto mt-6" />
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {blog.sections?.map((section, idx) => (
              <motion.section
                key={`blog-section-${idx}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * idx }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8"
              >
                <h2 className="font-serif text-xl sm:text-2xl text-gold mb-4">{section.heading}</h2>
                <div className="text-white/80 text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
                  {section.content}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t border-white/10">
            <p className="text-white/40 text-sm">Created with Triple A Travel Solutions</p>
            <a href="https://tripleabooking.com" target="_blank" rel="noopener noreferrer" className="text-gold text-sm hover:text-gold-light transition-colors">
              tripleabooking.com
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedBlogPage;
